import React, { useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { Plus } from "lucide-react";

// Real project photos from Black Rock Construction
// tagIndex maps to translations.gallery.tags array:
// 0=Before & After, 1=Drywall, 2=Painting, 3=Flooring, 4=Roofing
// We also support a custom "acoustic" tag below.

const ACOUSTIC_TAG = { en: "Acoustic Ceilings", es: "Plafones" };
const FRAMING_TAG = { en: "Framing", es: "Armazón" };
const FINISHING_TAG = { en: "Finishing", es: "Acabados" };

const PROJECTS = [
  // --- Framing (featured first) ---
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/xfyr7lbd_IMG_0205.png",
    tag: "framing",
    alt: "Metal stud framing in commercial space with drywall stacks",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/dczl23lf_IMG_0203.png",
    tag: "framing",
    alt: "Steel stud framing for new interior walls",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/sgpecbgx_IMG_0206.png",
    tag: "framing",
    alt: "Steel stud framing with stacked drywall ready for install",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/q32oj94f_IMG_3379.jpeg",
    tag: "framing",
    alt: "Metal framing for partition walls during build-out",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/7jjf1sjx_IMG_3378.jpeg",
    tag: "framing",
    alt: "Metal stud corridor framing during build-out",
  },
  // --- Finishing ---
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/v2tgzl87_IMG_2421.jpeg",
    tag: "finishing",
    alt: "Finished interior with custom pass-through opening and clean trim",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/thquy5ay_IMG_2423.jpeg",
    tag: "finishing",
    alt: "Smooth finished wall with custom window opening",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/imwrzskk_IMG_0216.png",
    tag: "finishing",
    alt: "Finished interior with painted doors and trim",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/hjsjex34_IMG_0208.png",
    tag: "finishing",
    alt: "Finished wall with mudded seams ready for primer",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/31xsxpxk_IMG_0209.png",
    tag: "finishing",
    alt: "Finished doorway opening with smooth wall transitions",
  },
  // --- Painting ---
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/un30ocam_IMG_3678.jpeg",
    tag: "painting",
    alt: "Freshly painted office walls in neutral gray tone",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/pg4u91mn_IMG_3675.jpeg",
    tag: "painting",
    alt: "Painted interior office room with light gray finish",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/zo8ga7i0_IMG_3672.jpeg",
    tag: "painting",
    alt: "Painted commercial corridor with completed wall finishing",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/81p63p6h_IMG_3673.jpeg",
    tag: "painting",
    alt: "Painted door frames and walls in office build-out",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/lde1vd5y_IMG_3679.jpeg",
    tag: "painting",
    alt: "Finished interior office with clean painted walls",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/3ol8h7nc_IMG_3571.jpeg",
    tag: "painting",
    alt: "Finished office hallway with painted neutral walls",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/cqs371ka_c64359e7-4348-4b4a-816d-78f6def31479.jpeg",
    tag: "painting",
    alt: "Painted open office space with clean wall finish",
  },
  // --- Acoustic Ceilings ---
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/3gnqszan_temp_image_422DAE89-BF1E-4C1C-A821-D17F97FD67E3.jpeg",
    tag: "acoustic",
    alt: "Acoustic ceiling installation with integrated LED lighting",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/k26b522z_temp_image_0A3A4948-29C6-493A-9F14-5F209B89F9A3.jpeg",
    tag: "acoustic",
    alt: "Laser-leveled acoustic ceiling installation",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/0x3n4jdv_temp_image_032DC356-D1BE-4096-BE82-C7BAB90847E7.jpeg",
    tag: "acoustic",
    alt: "Completed acoustic drop ceiling in commercial hallway",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/o804h7yl_temp_image_8BDF201F-4EEB-4E01-A551-DBF3C151F34B.jpeg",
    tag: "acoustic",
    alt: "Acoustic ceiling grid framework installation",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/j8otwsop_temp_image_885B7086-FEDD-4137-93A7-E8041FD3188A.jpeg",
    tag: "acoustic",
    alt: "Acoustic ceiling tile installation in progress",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/b711dh0b_temp_image_90600BA2-9474-46E9-808E-30AE48B57158.jpeg",
    tag: "acoustic",
    alt: "Acoustic ceiling with vent and recessed lighting",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/tqmseng2_temp_image_D5340947-963A-4C4C-9301-14785AB44156.jpeg",
    tag: "acoustic",
    alt: "Acoustic ceiling open grid before tile installation",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/lvtoprxr_temp_image_A153DEC7-37D9-4CAB-89C6-67909B702B67.jpeg",
    tag: "acoustic",
    alt: "Large commercial space with completed acoustic ceiling and lighting",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/xqxbpdte_temp_image_8CBC9780-400C-4B9B-9BB9-B86DCEA45C8E.jpeg",
    tag: "acoustic",
    alt: "Acoustic ceiling installed in commercial corridor with HVAC vent",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/dbh4mmwz_temp_image_7457DED4-A990-4726-8A25-E7AB53BAC81F.jpeg",
    tag: "acoustic",
    alt: "Open-plan space with full acoustic ceiling grid and lighting",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/dvxaepit_temp_image_47A64BBE-5FA7-4CF6-8604-16B3B88006A1.jpeg",
    tag: "acoustic",
    alt: "Completed acoustic ceiling spanning large open commercial space",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/hlftie86_temp_image_CE1EA133-5669-4F90-AEFF-7ACDC92DE80A.jpeg",
    tag: "acoustic",
    alt: "Acoustic ceiling grid framework partially tiled in large facility",
  },
  // --- Drywall ---
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/8phqik5q_IMG_2405.jpeg",
    tag: "drywall",
    alt: "Drywall installation around interior framing",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/59mfzs15_IMG_3517.jpeg",
    tag: "drywall",
    alt: "Drywall ceiling and wall finishing",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/p5e1zahd_temp_image_D1A26B5B-A483-4632-B1E6-5F26EB3D49C3.jpeg",
    tag: "drywall",
    alt: "Drywall sheet staging on construction site",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/17oux25s_IMG_3379.jpeg",
    tag: "drywall",
    alt: "Drywall mudding and taping work",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/a88a58s5_IMG_3377.jpeg",
    tag: "drywall",
    alt: "Finished drywall walls in office build-out",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/x5rx6lb1_IMG_3566.jpeg",
    tag: "drywall",
    alt: "Drywall partition walls in commercial space build-out",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/nvm9k4cz_IMG_3567.jpeg",
    tag: "drywall",
    alt: "Drywall pass-through window opening with smooth finish",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/5383t8mi_IMG_3568.jpeg",
    tag: "drywall",
    alt: "Drywall counter wall with custom pass-through opening",
  },
];

export default function Gallery() {
  const { t, lang } = useLang();
  const INITIAL = 12;
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? PROJECTS : PROJECTS.slice(0, INITIAL);
  const remaining = PROJECTS.length - INITIAL;

  const labelFor = (tag) => {
    if (tag === "framing") return FRAMING_TAG[lang] || FRAMING_TAG.en;
    if (tag === "finishing") return FINISHING_TAG[lang] || FINISHING_TAG.en;
    if (tag === "painting") return t.gallery.tags[2];
    if (tag === "acoustic") return ACOUSTIC_TAG[lang] || ACOUSTIC_TAG.en;
    if (tag === "drywall") return t.gallery.tags[1];
    return t.gallery.tags[0];
  };

  const loadMoreLabel = lang === "es"
    ? `Ver ${remaining} fotos más`
    : `View ${remaining} more photos`;

  return (
    <section id="gallery" data-testid="gallery-section" className="bg-neutral-950 py-24 sm:py-32 border-t border-neutral-900">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 text-orange-500 font-mono-label text-xs uppercase tracking-[0.25em] mb-5">
              <span className="w-8 h-px bg-orange-500" /> {t.gallery.label}
            </div>
            <h2 className="font-display uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] whitespace-pre-line">
              {t.gallery.title}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-neutral-400 text-base sm:text-lg leading-relaxed">{t.gallery.sub}</p>
            <div className="flex flex-wrap gap-2 mt-5">
              {[FRAMING_TAG[lang] || FRAMING_TAG.en, FINISHING_TAG[lang] || FINISHING_TAG.en, ACOUSTIC_TAG[lang] || ACOUSTIC_TAG.en, t.gallery.tags[1], t.gallery.tags[2], t.gallery.tags[3]].map((tag, i) => (
                <span key={i} className="px-3 py-1.5 text-xs font-mono-label uppercase tracking-wider border border-neutral-800 text-neutral-400 rounded-sm">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
          {visible.map((proj, i) => (
            <div
              key={i}
              data-testid={`gallery-item-${i}`}
              className={`group relative overflow-hidden rounded-sm border border-neutral-900 ${
                i === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-square"
              }`}
            >
              <img
                src={proj.src}
                alt={proj.alt}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
              <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                <span className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-orange-500">
                  {labelFor(proj.tag)}
                </span>
                <span className="font-display uppercase text-white text-sm">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
            </div>
          ))}
        </div>

        {!showAll && remaining > 0 && (
          <div className="mt-10 flex justify-center">
            <button
              data-testid="gallery-load-more"
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-black font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(249,115,22,0.35)]"
            >
              <Plus size={18} /> {loadMoreLabel}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
